import { LargeSelect } from '@/components/LargeSelect';
import { RoundedTab } from '@/components/RoundedTab';
import { SectionHeader } from '@/components/SectionHeader';
import Tabs from '@/components/Tabs';
import { Button, Form, Grid, Input, Modal, Select } from '@arco-design/web-react';
import { IconDelete, IconPlus } from '@arco-design/web-react/icon';
import { produce } from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AddedPods } from '.';
import { useContainerStore } from '../../container';
import { useConfigKeyMaps, useSecretKeymaps } from '../../hooks';
import { GrayBox } from '../../tools';
import { WorkloadComponent } from '../../types';
import {
  isVolumeKeymapItem,
  VolumeItem,
  VolumeKeymapItem,
  VolumeKeymapTab,
  VolumeMountItem,
  VolumeMountType,
} from '../_hooks';
import { VolumeOptionLabel } from './volume-option-label';

const FormItem = Form.Item;
const FormList = Form.List;
const TabPane = Tabs.TabPane;
const Row = Grid.Row;
const Col = Grid.Col;

interface AddKeymapModalProps extends WorkloadComponent {
  cluster: string;
  namespace: string;
  open?: boolean;
  onClose?: () => void;
  onOk?: (values: VolumeKeymapItem) => void;
  data?: VolumeItem;
}
/**
 * 挂载配置字典或保密字典
 */
export function AddKeymapModal({
  cluster,
  namespace,
  open,
  onClose,
  onOk,
  data,
}: AddKeymapModalProps) {
  const { t } = useTranslation();
  const msg = {
    mount_configmap_or_secret: t('components:mount.configmap.or.secret'),
    config_keymap: t('common:configmap'),
    secret_keymap: t('common:secret'),
    config_keymap_placeholder: t('components:select.configmap'),
    secret_keymap_placeholder: t('components:select.secret'),
    config_keymap_placeholder_desc: t('components:mount.configmap.to.container'),
    secret_keymap_placeholder_desc: t('components:mount.secret.to.container'),
    select_keys: t('components:select.specific.keys'),
    select_keys_desc: t('components:select.specific.keys.to.mount.to.containers'),
    key: t('common:key'),
    path: t('common:path'),
    add: t('common:add'),
  };

  const { containersArray } = useContainerStore();

  const editData = isVolumeKeymapItem(data) ? data : undefined;
  const initFormValues = useMemo(() => {
    /** 基于添加的容器列表关联名字 */
    const initVolumeMounts: VolumeMountItem[] = containersArray.map((container) => ({
      containerName: container.containerSetting.containerName,
      mountType: VolumeMountType.NotMount,
      mountPath: '',
    }));

    const initialValues: VolumeKeymapItem = {
      type: VolumeKeymapTab.CONFIG_MAP,
      selected: undefined,
      items: [],
      volumeMounts: initVolumeMounts,
      volumeName: undefined,
    };

    if (editData) {
      return produce(editData, (draft) => {
        draft.volumeMounts = containersArray.map((container) => {
          const existItem = draft.volumeMounts.find(
            (item) => item.containerName === container.containerSetting.containerName,
          );
          if (existItem) {
            return existItem;
          }
          return {
            containerName: container.containerSetting.containerName,
            mountType: VolumeMountType.NotMount,
            mountPath: '',
          };
        });
      });
    }

    return initialValues;
  }, [containersArray, editData]);

  const [allValues, setAllValues] = useState<Partial<VolumeKeymapItem>>(initFormValues);
  const [tab, setTab] = useState<VolumeKeymapTab>(initFormValues.type);

  const [form] = Form.useForm<VolumeKeymapItem>();
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setTab(VolumeKeymapTab.CONFIG_MAP);
    }
    // 编辑状态下，设置表单值
    if (open && editData) {
      form.setFieldsValue(initFormValues);
      setTab(editData.type);
    }
  }, [open, form, editData, initFormValues]);

  const { options: configOptionsData } = useConfigKeyMaps(cluster, namespace);
  const { options: secretOptionsData } = useSecretKeymaps(cluster, namespace);
  /**
   * 选择的配置字典或保密字典
   * 联动选择的特定的键选项
   */
  const [keysOptions, setKeysOptions] = useState<{ value: string; label: string }[]>([]);

  const configMapOptions = configOptionsData.map((item) => ({
    value: item.value,
    label: (
      <VolumeOptionLabel
        iconfontName="icon-a-peizhizidianmian"
        value={item.value}
        label={msg.config_keymap}
      />
    ),
    extraOptions: item.extraOptions,
  }));

  const secretOptions = secretOptionsData.map((item) => ({
    value: item.value,
    label: (
      <VolumeOptionLabel
        iconfontName="icon-a-baomizidianmian"
        value={item.value}
        label={msg.secret_keymap}
      />
    ),
    extraOptions: item.extraOptions,
  }));

  useEffect(() => {
    const selected = allValues.selected;
    if (!selected) return;

    if (tab === VolumeKeymapTab.CONFIG_MAP) {
      const config = configOptionsData.find((item) => item.value === selected);
      const targetKeys = config ? config.extraOptions : [];
      setKeysOptions(targetKeys);
    }
    if (tab === VolumeKeymapTab.SECRET_MAP) {
      const secret = secretOptionsData.find((item) => item.value === selected);
      const targetKeys = secret ? secret.extraOptions : [];
      setKeysOptions(targetKeys);
    }
  }, [tab, allValues, form, configOptionsData, secretOptionsData]);

  const onTabChange = (key: VolumeKeymapTab) => {
    // 1. 获取当前表单值
    const currentValues = form.getFieldsValue();
    // 2. 更新 allValues 状态，合并当前表单值
    setAllValues((prevValues) => ({ ...prevValues, ...currentValues, type: key }));
    // 3. 更新 tab
    setTab(key);
    // 4. 重置选择的选项
    form.resetFields(['selected', 'items']);
  };

  const handleOk = () => {
    const values = { ...allValues, type: tab };
    onOk?.(values as VolumeKeymapItem);
  };
  const items = form.getFieldValue('items') as VolumeKeymapItem['items'];
  // add 默认加一个[undefined, undefined]，所以这里要判断是否有值
  const disabledAddItems = items?.some((item) => !item?.key || !item?.path);

  return (
    <StyledModal
      visible={open}
      onCancel={onClose}
      onOk={handleOk}
      title={msg.mount_configmap_or_secret}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initFormValues}
        onValuesChange={(_, values) => {
          const allValues = { ...values, type: tab };
          setAllValues(allValues);
        }}
      >
        <RoundedTab activeTab={tab} onChange={onTabChange}>
          <TabPane key={VolumeKeymapTab.CONFIG_MAP} title={msg.config_keymap}>
            <FormItem field="selected">
              <LargeSelect
                placeholderNode={
                  <VolumeOptionLabel
                    iconfontName="icon-a-peizhizidianmian"
                    value={msg.config_keymap_placeholder}
                    label={msg.config_keymap_placeholder_desc}
                  />
                }
                options={configMapOptions}
              />
            </FormItem>
          </TabPane>
          <TabPane key={VolumeKeymapTab.SECRET_MAP} title={msg.secret_keymap}>
            <FormItem field="selected">
              <LargeSelect
                placeholderNode={
                  <VolumeOptionLabel
                    iconfontName="icon-a-baomizidianmian"
                    value={msg.secret_keymap_placeholder}
                    label={msg.secret_keymap_placeholder_desc}
                  />
                }
                options={secretOptions}
              />
            </FormItem>
          </TabPane>
        </RoundedTab>
        <AddedPods form={form} />
        <SectionHeader
          title={msg.select_keys}
          description={msg.select_keys_desc}
          titleStyle={{ fontWeight: 'normal' }}
          style={{ marginTop: '1rem' }}
        />
        <GrayBox>
          <FormList field="items">
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map((item, index) => {
                    return (
                      <Row key={index} gutter={8}>
                        <Col span={11}>
                          <FormItem field={`${item.field}.key`}>
                            <Select showSearch placeholder={msg.key} options={keysOptions} />
                          </FormItem>
                        </Col>
                        <Col span={11}>
                          <FormItem field={`${item.field}.path`}>
                            <Input placeholder={msg.path} />
                          </FormItem>
                        </Col>
                        <Col span={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            icon={<IconDelete />}
                            type="text"
                            status="danger"
                            onClick={() => remove(index)}
                          />
                        </Col>
                      </Row>
                    );
                  })}
                  <div>
                    <Button
                      icon={<IconPlus />}
                      type="primary"
                      size="mini"
                      disabled={disabledAddItems}
                      onClick={() => add()}
                    >
                      {msg.add}
                    </Button>
                  </div>
                </>
              );
            }}
          </FormList>
        </GrayBox>
      </Form>
    </StyledModal>
  );
}

/** ============================ 样式 ==================================== */
const StyledModal = styled(Modal)`
  width: 800px;
  .arco-modal-content {
    min-height: 400px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
`;
